import { DocumentNode, gql } from '@apollo/client';
import { useDisclosure } from '@chakra-ui/react';
import * as React from 'react';

import { event_registration_clicked } from '@/analytics';
import { LogInSignUpModal } from '@/components/LogInSignUp';
import { ERsvpResponse } from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';

import { RsvpMenu_EventFragment } from './graphql-types/RsvpMenu_EventFragment';

import { RsvpSelect } from './components/RsvpSelect';
import { useRsvpMenu } from './hooks/useRsvpMenu';

export const RsvpMenu: React.FC<{
  event: RsvpMenu_EventFragment;
  isDisabled?: boolean;
}> & { eventFragment: DocumentNode } = ({ event, isDisabled }) => {
  const current = useCurrent();
  const loginDisclosure = useDisclosure();
  const { isLoading, createRsvpForEvent, updateRsvpResponse } = useRsvpMenu();

  const currentUserRsvp =
    event.__typename === 'SimpleEventV2' && event.currentUserRsvp
      ? event.currentUserRsvp
      : undefined;

  const handleRsvpChange = async (rsvpResponse: ERsvpResponse) => {
    if (!current.user) {
      return loginDisclosure.onOpen();
    }

    try {
      const isRegistering =
        rsvpResponse === ERsvpResponse.GOING ||
        rsvpResponse === ERsvpResponse.MAYBE;
      if (isRegistering) {
        // Event user journey: member clicked RSVP Going/Maybe on an open simple event
        event_registration_clicked({
          entity_id: event._id,
          entity_type: event.isCourseWorkshop ? 'course_workshop' : 'simple_event',
          event_id: event._id,
          event_name: event.name,
          event_type: event.isCourseWorkshop ? 'course_workshop' : 'simple',
          event_start_date: event.startDate
            ? new Date(event.startDate).toISOString()
            : undefined,
          registration_source: 'rsvp',
        });
      }

      if (currentUserRsvp) {
        await updateRsvpResponse({
          variables: {
            input: {
              id: currentUserRsvp._id,
              response: rsvpResponse,
            },
          },
        });
      } else {
        await createRsvpForEvent({
          variables: {
            input: {
              attendeeId: current.user._id,
              eventId: event._id,
              response: rsvpResponse,
            },
          },
        });
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('rsvp-updated'));
      }
    } catch (e) {
      // noop
    }
  };

  return (
    <>
      <LogInSignUpModal
        isOpen={loginDisclosure.isOpen}
        onClose={loginDisclosure.onClose}
      />

      <RsvpSelect
        value={currentUserRsvp ? currentUserRsvp.response : undefined}
        onChange={handleRsvpChange}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
    </>
  );
};

RsvpMenu.eventFragment = gql`
  fragment RsvpMenu_EventFragment on EventV2 {
    _id
    name
    startDate
    isCourseWorkshop
    ... on SimpleEventV2 {
      currentUserRsvp {
        _id
        response
      }
    }
  }
`;
